import React, { useState } from 'react';
import { CheckCircle, Clock, AlertTriangle, PlayCircle, PauseCircle, ChevronDown } from 'lucide-react';

const statusConfig = {
  new: {
    label: 'New',
    icon: Clock,
    color: 'text-blue-500',
    bgColor: 'bg-blue-100'
  },
  in_progress: {
    label: 'In Progress',
    icon: PlayCircle,
    color: 'text-yellow-500',
    bgColor: 'bg-yellow-100'
  },
  on_hold: {
    label: 'On Hold',
    icon: PauseCircle,
    color: 'text-orange-500',
    bgColor: 'bg-orange-100'
  },
  completed: {
    label: 'Completed',
    icon: CheckCircle,
    color: 'text-green-500',
    bgColor: 'bg-green-100'
  },
  cancelled: {
    label: 'Cancelled',
    icon: AlertTriangle,
    color: 'text-red-500',
    bgColor: 'bg-red-100'
  }
};

const ProjectStatus = ({ status, onStatusChange, disabled = false }) => {
  const [isOpen, setIsOpen] = useState(false);

  const statusOptions = [
    { value: 'new', label: 'New', color: 'bg-blue-500' },
    { value: 'in-progress', label: 'In Progress', color: 'bg-yellow-500' },
    { value: 'scheduled', label: 'Scheduled', color: 'bg-purple-500' },
    { value: 'completed', label: 'Completed', color: 'bg-green-500' },
    { value: 'on-hold', label: 'On Hold', color: 'bg-red-500' }
  ];

  const currentStatus = statusOptions.find(option => option.value === status) || statusOptions[0];

  const handleStatusChange = (newStatus) => {
    if (!disabled && onStatusChange) {
      onStatusChange(newStatus);
    }
    setIsOpen(false);
  };

  return (
    <div className="relative">
      <button
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium text-white transition-colors ${
          disabled 
            ? 'opacity-50 cursor-not-allowed' 
            : 'cursor-pointer hover:opacity-80'
        } ${currentStatus.color}`}
      >
        <div className={`w-2 h-2 rounded-full ${currentStatus.color.replace('bg-', 'bg-')}`}></div>
        {currentStatus.label}
        {!disabled && <ChevronDown size={12} className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} />}
      </button>

      {isOpen && !disabled && (
        <div className="absolute top-full left-0 mt-1 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg shadow-lg z-10 min-w-[140px]">
          {statusOptions.map((option) => (
            <button
              key={option.value}
              onClick={() => handleStatusChange(option.value)}
              className={`w-full text-left px-3 py-2 text-sm hover:bg-[var(--bg-tertiary)] transition-colors flex items-center gap-2 ${
                option.value === status ? 'bg-[var(--bg-tertiary)]' : ''
              }`}
            >
              <div className={`w-2 h-2 rounded-full ${option.color}`}></div>
              <span className="text-[var(--text-primary)]">{option.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// New component for independent status tracking on shared projects
const IndependentProjectStatus = ({ status, onStatusChange, projectId }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [localStatus, setLocalStatus] = useState(status || 'new');

  const statusOptions = [
    { value: 'new', label: 'New', color: 'bg-blue-500' },
    { value: 'scheduled', label: 'Scheduled', color: 'bg-purple-500' },
    { value: 'completed', label: 'Completed', color: 'bg-green-500' }
  ];

  const currentStatus = statusOptions.find(option => option.value === localStatus) || statusOptions[0];

  const handleStatusChange = (newStatus) => {
    setLocalStatus(newStatus);
    if (onStatusChange) {
      onStatusChange(projectId, newStatus);
    }
    setIsOpen(false);
  };

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium text-white transition-colors cursor-pointer hover:opacity-80 ${currentStatus.color}`}
      >
        <div className={`w-2 h-2 rounded-full ${currentStatus.color.replace('bg-', 'bg-')}`}></div>
        {currentStatus.label}
        <ChevronDown size={12} className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 mt-1 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg shadow-lg z-10 min-w-[140px]">
          {statusOptions.map((option) => (
            <button
              key={option.value}
              onClick={() => handleStatusChange(option.value)}
              className={`w-full text-left px-3 py-2 text-sm hover:bg-[var(--bg-tertiary)] transition-colors flex items-center gap-2 ${
                option.value === localStatus ? 'bg-[var(--bg-tertiary)]' : ''
              }`}
            >
              <div className={`w-2 h-2 rounded-full ${option.color}`}></div>
              <span className="text-[var(--text-primary)]">{option.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export { ProjectStatus, IndependentProjectStatus }; 